// client/src/components/pages/WeighbridgeCompliance/NotificationConfig.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-4: the tab used to post one grouped object the server schema rejects
// (notification_type and recipients are required per row), so nothing was ever
// saved, and it read back fields no row has. The grouped form now maps to the
// server's rows: Calibration/Verification → CalibrationExpiry + VerificationExpiry,
// Maintenance → MaintenanceDue, Operator → OperatorCertExpiry, saved together with
// one PUT /notifications/config/asset/:assetId.
import ThemedSelect from '../../common/ThemedSelect';
import { useState, useEffect, useCallback } from 'react';
import { Bell, Settings, Mail, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import type { AxiosResponse } from 'axios';
import { formatAppDateTime } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import type {
  ComplianceAssetRow,
  ComplianceRequestError,
  NotificationConfigInput,
  NotificationConfigRow,
  NotificationLogRow,
  NotificationType,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

const ALERT_THRESHOLDS = [90, 60, 30, 14, 7];
const DEFAULT_THRESHOLDS = [30, 14, 7];

/** The grouped form the tab edits. Recipients are the comma-separated text as typed. */
export interface NotificationForm {
  calibrationExpiryEnabled: boolean;
  calibrationThresholds: number[];
  calibrationRecipients: string;
  maintenanceOverdueEnabled: boolean;
  maintenanceRecipients: string;
  operatorCertExpiryEnabled: boolean;
  operatorRecipients: string;
}

const initialConfig: NotificationForm = {
  calibrationExpiryEnabled: true,
  calibrationThresholds: DEFAULT_THRESHOLDS,
  calibrationRecipients: '',
  maintenanceOverdueEnabled: true,
  maintenanceRecipients: '',
  operatorCertExpiryEnabled: true,
  operatorRecipients: '',
};

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** 'a@x.com, b@y.com' → ['a@x.com', 'b@y.com'] (blank entries dropped). */
export const parseRecipients = (str: string): string[] =>
  str
    .split(/[,;]/)
    .map((r) => r.trim())
    .filter(Boolean);

/** The stored rows of one asset → the grouped form. No row → the section's default (on, no recipients). */
export function rowsToForm(rows: NotificationConfigRow[]): NotificationForm {
  const byType = new Map<NotificationType, NotificationConfigRow>();
  rows.forEach((r) => byType.set(r.notification_type, r));
  const cal = byType.get('CalibrationExpiry') || byType.get('VerificationExpiry');
  const maint = byType.get('MaintenanceDue');
  const op = byType.get('OperatorCertExpiry');
  const thresholds = cal
    ? String(cal.alert_days_before || '')
        .split(',')
        .map((d) => parseInt(d.trim(), 10))
        .filter((d) => !Number.isNaN(d))
        .sort((a, b) => b - a)
    : DEFAULT_THRESHOLDS;
  const enabled = (row: NotificationConfigRow | undefined) => (row ? Boolean(row.is_active) : true);
  return {
    calibrationExpiryEnabled: enabled(cal),
    calibrationThresholds: thresholds.length > 0 ? thresholds : DEFAULT_THRESHOLDS,
    calibrationRecipients: cal ? parseRecipients(cal.recipients || '').join(', ') : '',
    maintenanceOverdueEnabled: enabled(maint),
    maintenanceRecipients: maint ? parseRecipients(maint.recipients || '').join(', ') : '',
    operatorCertExpiryEnabled: enabled(op),
    operatorRecipients: op ? parseRecipients(op.recipients || '').join(', ') : '',
  };
}

/** The grouped form → the rows the server stores (CONTRACT.md, WBE-4). */
export function formToConfigs(
  form: NotificationForm,
  storedRows: NotificationConfigRow[] = []
): NotificationConfigInput[] {
  const recipients = (text: string) => parseRecipients(text).join(',');
  const days = [...form.calibrationThresholds].sort((a, b) => b - a).join(',');
  const storedOperator = storedRows.find((r) => r.notification_type === 'OperatorCertExpiry');
  const operator: NotificationConfigInput = {
    notification_type: 'OperatorCertExpiry',
    recipients: recipients(form.operatorRecipients),
    is_active: form.operatorCertExpiryEnabled,
  };
  // The tab has no operator thresholds: keep what is stored (the server default otherwise).
  if (storedOperator?.alert_days_before)
    operator.alert_days_before = storedOperator.alert_days_before;
  return [
    {
      notification_type: 'CalibrationExpiry',
      recipients: recipients(form.calibrationRecipients),
      alert_days_before: days,
      is_active: form.calibrationExpiryEnabled,
    },
    {
      notification_type: 'VerificationExpiry',
      recipients: recipients(form.calibrationRecipients),
      alert_days_before: days,
      is_active: form.calibrationExpiryEnabled,
    },
    {
      notification_type: 'MaintenanceDue',
      recipients: recipients(form.maintenanceRecipients),
      is_active: form.maintenanceOverdueEnabled,
    },
    operator,
  ];
}

/** Problems that stop a save, in the words the user sees. Empty = OK. */
export function validateForm(form: NotificationForm): string[] {
  const problems: string[] = [];
  const sections: Array<[boolean, string, string]> = [
    [
      form.calibrationExpiryEnabled,
      form.calibrationRecipients,
      'Calibration / Verification Expiry',
    ],
    [form.maintenanceOverdueEnabled, form.maintenanceRecipients, 'Maintenance Overdue'],
    [form.operatorCertExpiryEnabled, form.operatorRecipients, 'Operator Certification Expiry'],
  ];
  for (const [enabled, text, label] of sections) {
    const list = parseRecipients(text);
    if (enabled && list.length === 0) {
      problems.push(`${label}: add at least one email address, or switch these alerts off`);
    }
    const bad = list.filter((r) => !EMAIL_RE.test(r));
    if (bad.length > 0) problems.push(`${label}: not an email address: ${bad.join(', ')}`);
  }
  if (form.calibrationExpiryEnabled && form.calibrationThresholds.length === 0) {
    problems.push('Calibration / Verification Expiry: choose at least one alert threshold');
  }
  return problems;
}

/** The message of a failed request: the server's `error` plus its `details`. */
export function requestErrorMessage(error: unknown, fallback: string): string {
  const data = (error as ComplianceRequestError)?.response?.data;
  if (!data?.error) return fallback;
  return data.details && data.details.length > 0
    ? `${data.error}: ${data.details.join('; ')}`
    : data.error;
}

const getDeliveryBadge = (status: string | null | undefined) => {
  if (status === 'Sent') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-red-50 text-red-700 border-red-200">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Failed
    </span>
  );
};

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}

// Module-level (it was declared inside the component, so it re-mounted on every render).
const ToggleSwitch = ({ enabled, onChange, label, description }: ToggleSwitchProps) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <p className="text-sm font-medium text-slate-700">{label}</p>
      {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
        enabled ? 'bg-teal-500' : 'bg-slate-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

const SkeletonBlock = () => (
  <div className="space-y-3 animate-pulse">
    <div className="h-5 bg-slate-200 rounded w-1/3" />
    <div className="h-10 bg-slate-100 rounded" />
    <div className="h-5 bg-slate-200 rounded w-1/4" />
    <div className="h-10 bg-slate-100 rounded" />
  </div>
);

export const NotificationConfig = () => {
  const [assets, setAssets] = useState<ComplianceAssetRow[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [config, setConfig] = useState<NotificationForm>(initialConfig);
  const [storedRows, setStoredRows] = useState<NotificationConfigRow[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<NotificationLogRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(true);

  const fetchAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const response = (await api.assets.getAll({ limit: 100 })) as AxiosResponse<
        PaginatedBody<ComplianceAssetRow> | ComplianceAssetRow[]
      >;
      const d = response.data;
      setAssets(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      toast.error('Failed to load assets');
      console.error('Error fetching assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const fetchConfig = useCallback(async (assetId: string) => {
    if (!assetId) return;
    setLoadingConfig(true);
    try {
      const response = (await api.notifications.getConfig(assetId)) as AxiosResponse<
        NotificationConfigRow[]
      >;
      const rows = Array.isArray(response.data) ? response.data : [];
      setStoredRows(rows);
      setConfig(rowsToForm(rows));
    } catch (error) {
      // One message per failing endpoint, however many assets are picked (ST-00259).
      toast.error('Failed to load notification config', {
        toastId: 'compliance-notification-config-load',
      });
      console.error('Error fetching config:', error);
      setStoredRows([]);
      setConfig(initialConfig);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const fetchHistory = useCallback(async (assetId: string) => {
    if (!assetId) return;
    setLoadingHistory(true);
    try {
      const response = (await api.notifications.getLog({ assetId })) as AxiosResponse<
        PaginatedBody<NotificationLogRow> | NotificationLogRow[]
      >;
      const d = response.data;
      setHistory(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      toast.error('Failed to load notification history', {
        toastId: 'compliance-notification-history-load',
      });
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAssetId) {
      fetchConfig(selectedAssetId);
      fetchHistory(selectedAssetId);
    } else {
      setConfig(initialConfig);
      setStoredRows([]);
      setHistory([]);
    }
  }, [selectedAssetId, fetchConfig, fetchHistory]);

  const handleThresholdToggle = (days: number) => {
    setConfig((prev) => {
      const current = prev.calibrationThresholds;
      const next = current.includes(days)
        ? current.filter((d) => d !== days)
        : [...current, days].sort((a, b) => b - a);
      return { ...prev, calibrationThresholds: next };
    });
  };

  const handleSave = async () => {
    if (!selectedAssetId) {
      toast.error('Please select an asset first');
      return;
    }
    const problems = validateForm(config);
    if (problems.length > 0) {
      toast.error(problems.join('. '));
      return;
    }
    setSaving(true);
    try {
      const response = (await api.notifications.saveForAsset(selectedAssetId, {
        configs: formToConfigs(config, storedRows),
      })) as AxiosResponse<NotificationConfigRow[]>;
      if (Array.isArray(response.data)) {
        setStoredRows(response.data);
        setConfig(rowsToForm(response.data));
      }
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error(requestErrorMessage(error, 'Failed to save notification settings'));
      console.error('Error saving config:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Notification Settings
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure alert thresholds and email recipients per asset
          </p>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            Alerts are checked once a day at 07:00 (SAST)
          </p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
          Select Asset
        </label>
        <div className="relative max-w-md">
          {loadingAssets ? (
            <div className="h-10 bg-slate-100 rounded-lg animate-pulse" />
          ) : (
            <>
              <ThemedSelect
                value={selectedAssetId}
                onChange={(e) => setSelectedAssetId(String(e.target.value))}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
              >
                <option value="">Select an asset...</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || String(a.asset_name ?? '')}
                  </option>
                ))}
              </ThemedSelect>
            </>
          )}
        </div>
      </div>

      {!selectedAssetId ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-700 mb-1">
            Select an asset to configure
          </h3>
          <p className="text-sm text-slate-400 max-w-sm">
            Choose a weighbridge asset from the dropdown above to manage its notification settings
          </p>
        </div>
      ) : loadingConfig ? (
        <div className="bg-surface rounded-xl border border-slate-200 p-6">
          <SkeletonBlock />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-surface rounded-xl border border-slate-200 p-6 space-y-6">
            <div className="space-y-4">
              <ToggleSwitch
                enabled={config.calibrationExpiryEnabled}
                onChange={(val) => setConfig({ ...config, calibrationExpiryEnabled: val })}
                label="Calibration / Verification Expiry Alerts"
                description="Send alerts when calibration or verification certificates are approaching expiry"
              />
              {config.calibrationExpiryEnabled && (
                <div className="pl-4 border-l-2 border-teal-200 space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Alert Thresholds
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {ALERT_THRESHOLDS.map((days) => (
                        <label
                          key={days}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                            config.calibrationThresholds.includes(days)
                              ? 'border-teal-300 bg-teal-50 text-teal-700'
                              : 'border-slate-200 bg-surface text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={config.calibrationThresholds.includes(days)}
                            onChange={() => handleThresholdToggle(days)}
                            className="sr-only"
                          />
                          <span
                            className={`w-4 h-4 rounded border flex items-center justify-center ${
                              config.calibrationThresholds.includes(days)
                                ? 'bg-teal-500 border-teal-500'
                                : 'border-slate-300'
                            }`}
                          >
                            {config.calibrationThresholds.includes(days) && (
                              <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </span>
                          {days} days
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      Email Recipients
                    </label>
                    <input
                      type="text"
                      value={config.calibrationRecipients}
                      onChange={(e) =>
                        setConfig({ ...config, calibrationRecipients: e.target.value })
                      }
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      placeholder="email1@example.com, email2@example.com"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">
                      Separate multiple addresses with commas
                    </p>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-slate-100" />

            <div className="space-y-4">
              <ToggleSwitch
                enabled={config.maintenanceOverdueEnabled}
                onChange={(val) => setConfig({ ...config, maintenanceOverdueEnabled: val })}
                label="Maintenance Overdue Alerts"
                description="Send alerts when scheduled maintenance is overdue"
              />
              {config.maintenanceOverdueEnabled && (
                <div className="pl-4 border-l-2 border-teal-200">
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Email Recipients
                  </label>
                  <input
                    type="text"
                    value={config.maintenanceRecipients}
                    onChange={(e) =>
                      setConfig({ ...config, maintenanceRecipients: e.target.value })
                    }
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Separate multiple addresses with commas
                  </p>
                </div>
              )}
            </div>

            <hr className="border-slate-100" />

            <div className="space-y-4">
              <ToggleSwitch
                enabled={config.operatorCertExpiryEnabled}
                onChange={(val) => setConfig({ ...config, operatorCertExpiryEnabled: val })}
                label="Operator Certification Expiry"
                description="Send alerts when operator certifications are approaching expiry"
              />
              {config.operatorCertExpiryEnabled && (
                <div className="pl-4 border-l-2 border-teal-200">
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Email Recipients
                  </label>
                  <input
                    type="text"
                    value={config.operatorRecipients}
                    onChange={(e) => setConfig({ ...config, operatorRecipients: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Separate multiple addresses with commas
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Settings className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div>
            <h3
              className="text-sm font-bold text-slate-700 mb-3"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Notification History
            </h3>
            <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-100">
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Date
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Recipient
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Subject
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Related Entity
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Status
                      </th>
                      <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingHistory ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 6 }).map((_, j) => (
                            <td key={j} className="px-5 py-3.5">
                              <div
                                className="h-4 bg-slate-200 rounded animate-pulse"
                                style={{ width: `${50 + Math.random() * 50}%` }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : history.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                              <Mail className="w-6 h-6 text-slate-300" />
                            </div>
                            <h3 className="text-sm font-semibold text-slate-700 mb-1">
                              No notifications sent yet
                            </h3>
                            <p className="text-xs text-slate-400">
                              Notification history will appear here once alerts are triggered
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      history.map((item, idx) => (
                        <tr
                          key={item.id || idx}
                          className="hover:bg-primary-50/30 transition-colors"
                        >
                          <td className="px-5 py-3.5 text-sm text-slate-700">
                            {item.sent_at ? formatAppDateTime(item.sent_at) : '-'}
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-700">
                            {item.recipient || '-'}
                          </td>
                          <td
                            className="px-5 py-3.5 text-sm text-slate-700 max-w-[250px] truncate"
                            title={item.subject || undefined}
                          >
                            {item.subject || '-'}
                          </td>
                          {/* The log row's linked entity type (Calibration, Verification, Operator, Maintenance). */}
                          <td className="px-5 py-3.5 text-sm text-slate-700">
                            {item.linked_entity_type || '-'}
                          </td>
                          <td className="px-5 py-3.5">{getDeliveryBadge(item.status)}</td>
                          <td
                            className="px-5 py-3.5 text-sm text-red-500 max-w-[200px] truncate"
                            title={item.error_message || ''}
                          >
                            {item.error_message || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
